import { useState } from "react";
import { Copy, Check, Share2, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { DropdownMenuItem } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { API, BACKEND_URL } from "../lib/helpers";
import axios from "axios";

const ShareDeal = ({ property, analysis, variant = "icon" }) => {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const shareText = analysis
    ? `${property.address}, ${property.city}, ${property.state} | $${(property.price || 0).toLocaleString()} | Rent: $${(analysis.estimated_monthly_rent || 0).toLocaleString()}/mo | Cash Flow: $${(analysis.cash_flow_monthly || 0).toLocaleString()}/mo | Score: ${analysis.deal_score}/100`
    : `${property.address}, ${property.city}, ${property.state} | $${(property.price || 0).toLocaleString()}`;

  const createShareLink = async () => {
    try {
      const res = await axios.post(`${API}/shared-deals`, { property, analysis });
      // Use the share_id (which is now the property ID) for a clean URL
      return `${BACKEND_URL}/deal/${res.data.share_id}`;
    } catch {
      // Fallback: use property ID directly even if the save fails
      const propId = property.id || property.zpid;
      return propId ? `${BACKEND_URL}/deal/${propId}` : BACKEND_URL;
    }
  };

  const handleCopy = async (e) => {
    e?.stopPropagation();
    setSharing(true);
    try {
      const url = await createShareLink();
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Share link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    } finally {
      setSharing(false);
    }
  };

  const handleTwitter = async (e) => {
    e?.stopPropagation();
    setSharing(true);
    try {
      const url = await createShareLink();
      const text = encodeURIComponent(`${shareText} — via @RealixData`);
      window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`, "_blank", "width=600,height=400");
    } catch {
      toast.error("Failed to share");
    } finally {
      setSharing(false);
    }
  };

  const handleFacebook = async (e) => {
    e?.stopPropagation();
    setSharing(true);
    try {
      const url = await createShareLink();
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank", "width=600,height=400");
    } catch {
      toast.error("Failed to share");
    } finally {
      setSharing(false);
    }
  };

  const handleReddit = async (e) => {
    e?.stopPropagation();
    setSharing(true);
    try {
      const url = await createShareLink();
      const title = encodeURIComponent(`${property.address} — $${(analysis?.cash_flow_monthly || 0).toLocaleString()}/mo cash flow, ${(analysis?.cap_rate || 0).toFixed(1)}% cap rate`);
      window.open(`https://reddit.com/submit?url=${encodeURIComponent(url)}&title=${title}`, "_blank", "width=600,height=400");
    } catch {
      toast.error("Failed to share");
    } finally {
      setSharing(false);
    }
  };

  const handleNativeShare = async (e) => {
    e?.stopPropagation();
    if (navigator.share) {
      setSharing(true);
      try {
        const url = await createShareLink();
        await navigator.share({
          title: `${property.address} — RealixData`,
          text: shareText,
          url,
        });
      } catch {
        // User cancelled
      } finally {
        setSharing(false);
      }
    } else {
      handleCopy(e);
    }
  };

  if (variant === "icon") {
    return (
      <button
        onClick={handleNativeShare}
        disabled={sharing}
        className="p-2 sm:p-1.5 rounded-lg bg-black/50 hover:bg-black/70 transition-colors text-white"
        title="Share deal"
        data-testid={`share-btn-${property.id}`}
      >
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4" />}
      </button>
    );
  }

  if (variant === "dropdown") {
    return (
      <>
        <DropdownMenuItem onClick={handleCopy} data-testid="share-menu-copy-link">
          {copied ? <Check className="w-3.5 h-3.5 mr-2 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 mr-2" />}
          {copied ? "Link Copied!" : "Copy Share Link"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleTwitter} data-testid="share-menu-twitter">
          <svg className="w-3.5 h-3.5 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          Post on X / Twitter
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleFacebook} data-testid="share-menu-facebook">
          <svg className="w-3.5 h-3.5 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          Share on Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleReddit} data-testid="share-menu-reddit">
          <svg className="w-3.5 h-3.5 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
          Post on Reddit
        </DropdownMenuItem>
      </>
    );
  }

  return (
    <div className="flex gap-1" data-testid={`share-actions-${property.id}`}>
      <Button variant="outline" size="sm" onClick={handleCopy} disabled={sharing} className="text-xs h-7 px-2">
        {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
      </Button>
      <Button variant="outline" size="sm" onClick={handleTwitter} disabled={sharing} className="text-xs h-7 px-2" title="Share on X/Twitter" data-testid="share-twitter">
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      </Button>
      <Button variant="outline" size="sm" onClick={handleFacebook} disabled={sharing} className="text-xs h-7 px-2" title="Share on Facebook" data-testid="share-facebook">
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      </Button>
    </div>
  );
};

export default ShareDeal;
